import { useState, useEffect, useCallback } from 'react';
import { addCsrfTokenToConfig } from '../utils/csrfUtils';
import { fetchAllWarranties, updateWarrantyStatus } from '../components/utils/api';

/**
 * 保養單管理自定義Hook
 * 用於處理保養單數據的獲取、過濾和狀態更新
 * 
 * @param {string} token - 認證令牌
 * @returns {Object} 保養單相關狀態和方法
 */
function useWarranty(token) {
    // 保養單數據狀態
    const [warranties, setWarranties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [updateError, setUpdateError] = useState(null);

    // 過濾和搜索狀態
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('Pending');

    /**
     * 獲取所有保養單數據
     */
    const fetchWarranties = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            };

            // 添加CSRF令牌
            const configWithCsrf = addCsrfTokenToConfig(config);

            const response = await fetch('/api/customers/warranties', {
                headers: configWithCsrf.headers
            });

            if (!response.ok) {
                throw new Error(`獲取保養單失敗: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();

            // 嘗試從不同的響應格式中提取保養單數據
            let extractedWarranties = [];

            // 情況1: 響應是數組
            if (Array.isArray(data)) {
                extractedWarranties = data;
            }
            // 情況2: 響應是對象，有warranties屬性
            else if (data && data.warranties && Array.isArray(data.warranties)) {
                extractedWarranties = data.warranties;
            }
            // 情況3: 響應是對象，有data屬性
            else if (data && data.data && Array.isArray(data.data)) {
                extractedWarranties = data.data;
            }
            // 情況4: 響應是對象，有results屬性
            else if (data && data.results && Array.isArray(data.results)) {
                extractedWarranties = data.results;
            }
            // 情況5: 響應是對象，有保養單相關屬性
            else if (data && typeof data === 'object') {
                // 檢查響應中的每個屬性，尋找可能的保養單數組
                for (const key in data) {
                    if (Array.isArray(data[key])) {
                        const possibleWarranties = data[key];
                        if (possibleWarranties.length > 0 && possibleWarranties[0] &&
                            (possibleWarranties[0].warranty_period !== undefined ||
                                possibleWarranties[0].verification_status !== undefined ||
                                possibleWarranties[0].product_model !== undefined)) {
                            extractedWarranties = possibleWarranties;
                            break;
                        }
                    }
                }
            }

            setWarranties(extractedWarranties);
        } catch (err) {
            setError(err.message || '獲取保養單失敗');
            console.error('獲取保養單錯誤:', err);
        } finally {
            setLoading(false);
        }
    }, [token]);

    /**
     * 獲取單個保養單詳情
     * @param {string} warrantyId - 保養單ID
     * @returns {Promise<Object|null>} 保養單詳情或null
     */
    const fetchWarrantyById = useCallback(async (warrantyId) => {
        if (!token || !warrantyId) return null;

        setLoading(true);
        setError(null);

        try {
            // 處理不同格式的ID
            let id = warrantyId;
            
            // 從 Shopify Metaobject ID 中提取純數字 ID
            if (typeof warrantyId === 'string' && warrantyId.startsWith('gid://shopify/Metaobject/')) {
                id = warrantyId.split('/').pop();
                console.log('從Shopify ID提取數字部分:', id);
            }

            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            };

            // 添加CSRF令牌
            const configWithCsrf = addCsrfTokenToConfig(config);
            
            console.log(`發送請求獲取保養單: /api/customers/warranty?id=${id}`);

            const response = await fetch(`/api/customers/warranty?id=${id}`, {
                headers: configWithCsrf.headers
            });

            // 檢查內容類型以確認是JSON還是HTML
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('API返回非JSON響應:', text.substring(0, 200));
                throw new Error('API返回了無效的響應格式，請檢查API服務是否正常');
            }

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || `獲取保養單失敗: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();

            // 檢查是否有正確的保養單數據結構
            if (!data) {
                throw new Error('獲取保養單詳情時出錯: 數據為空');
            }

            // 容錯處理：如果API返回結構不同，嘗試從不同位置獲取保養單數據
            const warranty = data.warranty || data;
            
            return warranty;
        } catch (err) {
            console.error('獲取保養單詳情時出錯:', err);
            setError(err.message || '獲取保養單詳情時出錯');
            return null;
        } finally {
            setLoading(false);
        }
    }, [token]);

    /**
     * 更新保養單狀態
     * @param {string|number} warrantyId - 保養單ID
     * @param {Object} updateData - 更新數據
     * @returns {Promise<boolean>} - 成功返回 true，失敗返回 false
     */
    const updateStatus = async (warrantyId, updateData) => {
        // console.log(`useWarranty: 開始更新保養單狀態: ID=${warrantyId}, 數據=`, updateData);
        setUpdateLoading(true);
        setUpdateError(null);

        try {
            // 調用 API 更新保養單狀態
            const result = await updateWarrantyStatus(warrantyId, updateData, token);
            // console.log(`useWarranty: 保養單狀態更新結果:`, result);

            // 更新本地狀態
            if (result === true) {
                // 更新保養單列表中的對應保養單
                setWarranties(prev => {
                    if (!prev) return null;

                    return prev.map(w => {
                        if (w.id === warrantyId) {
                            return {
                                ...w,
                                verification_status: updateData.status,
                                verification_notes: updateData.notes,
                                staff_name: updateData.staff_name,
                                verification_date: updateData.verification_date,
                                warranty_expiration_date: updateData.warranty_expiration_date
                            };
                        }
                        return w;
                    });
                });

                setUpdateLoading(false);
                return true;
            } else {
                throw new Error('保養單狀態更新失敗');
            }
        } catch (error) {
            console.error('useWarranty: 保養單狀態更新過程中發生錯誤:', error);
            setUpdateError(error.message || '保養單狀態更新失敗');
            setUpdateLoading(false);
            return false;
        }
    };

    // 初始加載
    useEffect(() => {
        if (token) {
            fetchWarranties();
        }
    }, [token, fetchWarranties]);

    // 根據搜索詞過濾保養單
    const filteredWarranties = Array.isArray(warranties)
        ? warranties.filter(warranty => {
            if (!warranty) {
                return false;
            }

            // 搜索詞篩選
            const searchMatch =
                (warranty.customer_name && warranty.customer_name.toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
                (warranty.phone && warranty.phone.toString().includes(searchTerm)) ||
                (warranty.product_name && warranty.product_name.toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
                (warranty.handle && warranty.handle.toString().toLowerCase().includes(searchTerm.toLowerCase()));

            return searchMatch;
        })
        : [];

    // 按照驗證狀態為保養單分組
    const groupedWarranties = {
        Pending: filteredWarranties.filter(w => w.verification_status === 'Pending' || w.verification_status === null || w.verification_status === undefined),
        Approved: filteredWarranties.filter(w => w.verification_status === 'Approved'),
        Rejected: filteredWarranties.filter(w => w.verification_status === 'Rejected')
    };

    // 格式化日期顯示
    const formatDate = (dateString) => {
        if (!dateString) return '未設置';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('zh-TW');
        } catch (e) {
            return dateString || '未設置';
        }
    };

    // 獲取狀態的中文名稱
    const getStatusName = (status) => {
        switch (status) {
            case 'Pending': return '待驗證';
            case 'Approved': return '已通過';
            case 'Rejected': return '已拒絕';
            case null:
            case undefined:
                return '待驗證';
            default: return '待驗證';
        }
    };

    // 獲取狀態的CSS類名
    const getStatusClass = (status) => {
        switch (status) {
            case 'Pending': return 'status-pending';
            case 'Approved': return 'status-approved';
            case 'Rejected': return 'status-rejected';
            case null:
            case undefined:
                return 'status-pending';
            default: return 'status-pending';
        }
    };

    // 格式化產品名稱
    const formatProductName = (productName) => {
        if (!productName) return '未設置';
        return productName.length > 30 ? productName.substring(0, 30) + '...' : productName;
    };

    return {
        warranties,
        loading,
        error,
        updateLoading,
        updateError,
        searchTerm,
        setSearchTerm,
        filterStatus,
        setFilterStatus,
        fetchWarranties,
        fetchWarrantyById,
        updateStatus,
        groupedWarranties,
        formatDate,
        getStatusName,
        getStatusClass,
        formatProductName
    };
}

export default useWarranty; 